import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';

const DOMAINS = [
  'core', 'scheduling', 'users', 'jobs', 'alerts', 'chemicals',
  'pools', 'equipment', 'service_levels', 'inventory', 'tech_pay',
  'operations', 'invoicing',
];

const DARK = {
  page: '#0f172a', surface: '#1e293b', surfaceDeep: '#162032',
  border: '#334155', text: '#f1f5f9', textSecondary: '#94a3b8',
  textMuted: '#64748b', inputBg: '#0f172a', codeBg: '#0a1628',
  codeText: '#4ade80', activeBg: '#2563eb', hoverBg: '#162032',
  successBg: '#052e16', successBorder: '#166534', successText: '#4ade80',
  dangerBg: '#450a0a', dangerBorder: '#7f1d1d', dangerText: '#fca5a5',
};
const LIGHT = {
  page: '#f8fafc', surface: '#ffffff', surfaceDeep: '#f1f5f9',
  border: '#e2e8f0', text: '#0f172a', textSecondary: '#475569',
  textMuted: '#94a3b8', inputBg: '#f8fafc', codeBg: '#f1f5f9',
  codeText: '#16a34a', activeBg: '#2563eb', hoverBg: '#f1f5f9',
  successBg: '#f0fdf4', successBorder: '#86efac', successText: '#166534',
  dangerBg: '#fef2f2', dangerBorder: '#fca5a5', dangerText: '#991b1b',
};

const FeedbackCorrections = ({ dark: darkProp = true }) => {
  const t = darkProp ? DARK : LIGHT;
  const [stats, setStats] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/feedback-cache/stats`);
      const data = await res.json();
      if (data.success) setStats(data.data || []);
      else setError(data.error || 'Failed to load stats');
    } catch (e) { setError('Cannot reach API server.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadEntries = useCallback(async () => {
    if (!selectedDomain) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: '25', active_only: 'false' });
      if (search) params.append('search', search);
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/feedback-cache/${selectedDomain}?${params}`);
      const data = await res.json();
      if (data.success) { setEntries(data.data.records || []); setPagination(data.data); }
      else setError(data.error);
    } catch (e) { setError('Cannot reach API server.'); }
    finally { setLoading(false); }
  }, [selectedDomain, page, search]);

  useEffect(() => { if (selectedDomain) loadEntries(); }, [loadEntries, selectedDomain]);

  const toggleActive = async (entryId) => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/feedback-cache/${selectedDomain}/${entryId}/toggle`, { method: 'POST' });
      const data = await res.json();
      if (data.success) setEntries(prev => prev.map(e => e.id === entryId ? { ...e, is_active: data.is_active } : e));
    } catch (e) { console.error('Toggle failed:', e); }
  };

  const onSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };

  if (!selectedDomain) {
    return (
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 32px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: t.text }}>Domain Feedback Cache</h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: t.textMuted }}>Select a domain to view and manage validated SQL corrections.</p>
        {error && <div style={{ padding: '12px 16px', background: t.dangerBg, border: `1px solid ${t.dangerBorder}`, borderRadius: 8, color: t.dangerText, fontSize: 14, marginBottom: 20 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {DOMAINS.map(domain => {
            const s = stats.find(x => x.domain === domain) || {};
            return (
              <button key={domain} onClick={() => { setSelectedDomain(domain); setPage(1); setSearch(''); setSearchInput(''); }}
                style={{ padding: '20px', background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.text, textTransform: 'capitalize', marginBottom: 12 }}>{domain.replace('_', ' ')}</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                  <div><span style={{ color: t.textMuted }}>Active: </span><span style={{ color: t.successText, fontWeight: 600 }}>{s.active || 0}</span></div>
                  <div><span style={{ color: t.textMuted }}>Inactive: </span><span style={{ color: t.dangerText, fontWeight: 600 }}>{s.inactive || 0}</span></div>
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8 }}>Total: {s.total || 0}{s.last_updated && ` | Last: ${new Date(s.last_updated).toLocaleDateString()}`}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => { setSelectedDomain(null); setEntries([]); loadStats(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', color: t.textSecondary, fontSize: 13, fontWeight: 500 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg> All Domains
        </button>
        <span style={{ color: t.textMuted }}>/</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: t.text, textTransform: 'capitalize' }}>{selectedDomain.replace('_', ' ')}</span>
        <span style={{ fontSize: 13, color: t.textMuted, marginLeft: 8 }}>{pagination.total || 0} entries</span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <form onSubmit={onSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
          <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search by query text..."
            style={{ flex: 1, padding: '8px 14px', background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 14, outline: 'none' }} />
          <button type="submit" style={{ padding: '8px 16px', background: t.activeBg, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Search</button>
        </form>
        <button onClick={loadEntries} style={{ padding: '8px 16px', background: darkProp ? '#1d4ed820' : '#eff6ff', border: `1px solid ${darkProp ? '#1d4ed840' : '#bfdbfe'}`, borderRadius: 8, cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 500 }}>Refresh</button>
      </div>

      {error && <div style={{ padding: '12px 16px', background: t.dangerBg, border: `1px solid ${t.dangerBorder}`, borderRadius: 8, color: t.dangerText, fontSize: 14, marginBottom: 20 }}>{error}</div>}

      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 120px 80px', gap: 16, padding: '12px 20px', background: t.surfaceDeep, borderBottom: `1px solid ${t.border}`, fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>Query</span><span>Company ID</span><span>Type</span><span>Status</span><span>Actions</span>
        </div>

        {loading && entries.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>Loading...</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>No feedback entries in this domain yet.</div>
        ) : entries.map((entry) => (
          <div key={entry.id}>
            <div onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 120px 80px', gap: 16, padding: '14px 20px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, background: expanded === entry.id ? t.hoverBg : 'transparent', transition: 'background 0.1s' }}>
              <span style={{ fontSize: 14, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.normalized_query || entry.question || '—'}</span>
              <span style={{ fontSize: 13, color: t.textSecondary }}>{entry.company_id || 'all'}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: entry.feedback_type === 'thumbs_down' ? '#f59e0b' : '#3b82f6' }}>{entry.feedback_type || '—'}</span>
              <span><span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: entry.is_active ? t.successBg : t.dangerBg, border: `1px solid ${entry.is_active ? t.successBorder : t.dangerBorder}`,
                color: entry.is_active ? t.successText : t.dangerText }}>{entry.is_active ? 'Active' : 'Inactive'}</span></span>
              <span><button onClick={(e) => { e.stopPropagation(); toggleActive(entry.id); }}
                style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, border: 'none',
                  background: entry.is_active ? t.dangerBg : t.successBg, color: entry.is_active ? t.dangerText : t.successText }}>
                {entry.is_active ? 'Disable' : 'Enable'}</button></span>
            </div>
            {expanded === entry.id && (
              <div style={{ padding: '16px 20px', background: t.surfaceDeep, borderBottom: `1px solid ${t.border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Full Question</div>
                    <div style={{ padding: '10px 14px', background: t.codeBg, borderRadius: 8, fontSize: 13, color: t.text, lineHeight: 1.5 }}>{entry.question || entry.normalized_query || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Validated SQL</div>
                    <div style={{ padding: '10px 14px', background: t.codeBg, borderRadius: 8, fontSize: 12, color: t.codeText, fontFamily: 'monospace', lineHeight: 1.5, maxHeight: 150, overflowY: 'auto', wordBreak: 'break-all' }}>{entry.validated_sql || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: 12, color: t.textMuted }}>
                  <span>Domain: <strong style={{ color: t.textSecondary }}>{entry.domain || selectedDomain}</strong></span>
                  <span>Model: <strong style={{ color: t.textSecondary }}>{entry.model_used || '—'}</strong></span>
                  <span>Usage: <strong style={{ color: t.textSecondary }}>{entry.usage_count || 0}x</strong></span>
                  <span>Created: <strong style={{ color: t.textSecondary }}>{entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}</strong></span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button disabled={page <= 1} onClick={() => setPage(1)} style={pgBtn(t, page <= 1)}>First</button>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={pgBtn(t, page <= 1)}>Prev</button>
          <span style={{ fontSize: 13, color: t.textSecondary, padding: '0 12px' }}>Page {page} of {pagination.total_pages}</span>
          <button disabled={page >= pagination.total_pages} onClick={() => setPage(p => p + 1)} style={pgBtn(t, page >= pagination.total_pages)}>Next</button>
          <button disabled={page >= pagination.total_pages} onClick={() => setPage(pagination.total_pages)} style={pgBtn(t, page >= pagination.total_pages)}>Last</button>
        </div>
      )}
    </div>
  );
};

const pgBtn = (t, disabled) => ({
  padding: '6px 14px', fontSize: 13, fontWeight: 500,
  background: disabled ? 'transparent' : t.surface,
  border: `1px solid ${t.border}`, borderRadius: 8,
  color: disabled ? t.textMuted : t.textSecondary,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});

export default FeedbackCorrections;
